// Cloudflare Pages static deploy — no backend at runtime.
// All "fetch*" functions resolve from local CMS-friendly content, but keep
// their async signatures so calling components do not need to change.
//
// Source of truth: frontend/src/content/. Webpack inlines every JSON file
// at build time via require.context (CRA/Webpack 5 — NOT import.meta.glob,
// which is Vite-only). Editing a JSON via the future CMS triggers a normal
// CRA rebuild — no SSR, no runtime fetch.

import CATEGORIES_RAW from "../content/categories.json";
import BRANDS_RAW from "../content/brands.json";
import { generateProductSpecs } from "./specs";

// ───────── Content loading (build-time via require.context) ────────────────

const productCtx = require.context("../content/products", false, /\.json$/);
const PRODUCTS = productCtx
  .keys()
  .map((k) => productCtx(k))
  .filter((p) => p.active !== false)
  // `order` was injected at migration time to preserve the original Ulaş-first
  // layout. require.context returns files alphabetically, so this sort matters.
  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const blogCtx = require.context("../content/blog", false, /\.json$/);
const BLOG_POSTS = blogCtx
  .keys()
  .map((k) => blogCtx(k))
  .filter((p) => p.published !== false);

// Decap CMS edits list-of-objects files when they are wrapped in {items: […]}.
// Accept both shapes so a hand-edited bare array still works.
const unwrap = (raw) => (Array.isArray(raw) ? raw : raw && Array.isArray(raw.items) ? raw.items : []);

const CATEGORIES = unwrap(CATEGORIES_RAW).filter((c) => c.active !== false);

// brands.json stores [{name, active}, …]; support legacy string entries too.
const BRANDS = unwrap(BRANDS_RAW)
  .filter((b) => (typeof b === "string" ? true : b.active !== false))
  .map((b) => (typeof b === "string" ? b : b.name));

// ───────── Asset URL helpers ─────────────────────────────────────────────────

// Product images are bundled into public/products/ and served statically.
export const imgUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/static/products/")) {
    return path.replace("/api/static/products/", "/products/");
  }
  // Already a public path (/products/..., /videos/..., etc.) — return as-is.
  if (path.startsWith("/")) return path;
  // Bare filename — assume a product asset.
  return `/products/${path}`;
};

// Videos & their posters are bundled into public/videos/.
export const videoUrl = (filename) => `/videos/${filename}`;

// PDFs were generated by the backend (reportlab). Without a backend we can't
// produce them on-the-fly, so we return a sentinel that the UI can detect.
export const productPdfUrl = () => "#";

// ───────── Data helpers (sync under the hood, async signature kept) ──────────

// Strip diacritics so "Ulaş" matches "ulas", "Étui" matches "etui", etc.
const normalize = (s) =>
  s.toString().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

const matchesSearch = (product, query) => {
  if (!query) return true;
  const q = normalize(query).trim();
  if (!q) return true;
  const haystack = normalize(
    [
      product.name,
      product.brand,
      product.category,
      product.category_label,
      product.hook,
      product.short_label,
    ]
      .filter(Boolean)
      .join(" ")
  );
  return haystack.includes(q);
};

export const fetchCategories = async () => CATEGORIES;

export const fetchProducts = async (params = {}) => {
  let result = PRODUCTS;
  if (params.category && params.category !== "all") {
    result = result.filter((p) => p.category === params.category);
  }
  if (params.brand && params.brand !== "all") {
    result = result.filter((p) => p.brand === params.brand);
  }
  if (params.search) {
    result = result.filter((p) => matchesSearch(p, params.search));
  }
  if (typeof params.limit === "number") {
    result = result.slice(0, params.limit);
  }
  return result;
};

export const fetchBrands = async (category) => {
  if (category && category !== "all") {
    const filtered = PRODUCTS.filter((p) => p.category === category && p.brand);
    return Array.from(new Set(filtered.map((p) => p.brand)));
  }
  return BRANDS;
};

export const fetchProduct = async (slug) => {
  const p = PRODUCTS.find((p) => p.slug === slug);
  if (!p) throw new Error(`Product not found: ${slug}`);
  return p;
};

export const fetchRelated = async (slug, limit = 4) => {
  const p = PRODUCTS.find((p) => p.slug === slug);
  if (!p) return [];
  return PRODUCTS.filter((other) => other.category === p.category && other.slug !== slug).slice(
    0,
    limit
  );
};

// Prudent local fact-sheet — see lib/specs.js. We do not port the old
// md5-based deterministic_specs() because those values (GTIN, palettisation,
// poids) were fabricated and shouldn't be exposed as if they were real.
export const fetchSpecs = async (slug) => {
  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return null;
  return generateProductSpecs(product);
};

export const fetchBlog = async () =>
  [...BLOG_POSTS].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

export const fetchBlogPost = async (slug) => {
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) throw new Error(`Blog post not found: ${slug}`);
  return post;
};

// Contact form is handled client-side via a mailto: link directly in
// pages/Contact.jsx — no API call needed on Cloudflare Pages.
